import { useId, useState } from 'react'
import type { RespostaOtimizador } from '@baiocchi/shared'
import {
  Alerta,
  AreaTexto,
  Botao,
  CabecalhoCartao,
  Cartao,
  Etiqueta,
  useAvisos,
} from '@baiocchi/ui'
import { useMutation, useQuery } from '@tanstack/react-query'

import { chaves } from '../../api/chaves'
import { metaAdsApi } from '../../api/meta-ads'
import { IconeFaisca } from '../../componentes/icones'
import { useFiltrosUrl } from '../../ganchos/usar-filtros-url'
import { CabecalhoPagina } from '../../layout/layout-app'
import { FiltrosMetaAds } from './filtros'

/**
 * TELA-META-08 — Pergunte ao Otimizador (§15).
 *
 * ## A resposta é auditável, e isso é metade do produto
 *
 * `dadosUsados` mostra exatamente qual recorte foi consultado. Sem isso, a primeira resposta
 * que soar estranha faz o gestor desconfiar de todas — e ele tem razão em desconfiar de um
 * número que não pode conferir. Com isso, ele vê "2 contas, 1 a 7 de agosto, 14 anúncios com
 * insight" e sabe do que a resposta está falando.
 *
 * ## "Não tenho dados suficientes" é uma resposta boa
 *
 * `semDadosSuficientes` vem decidido **pelo servidor**, antes de qualquer chamada de IA.
 * Quando não há dado, a frase padrão aparece e nenhuma requisição paga é gasta — e, mais
 * importante, a resposta é sempre a mesma frase, em vez de o modelo improvisar uma versão
 * diferente a cada vez.
 *
 * ## O histórico é da sessão, não do banco
 *
 * As perguntas ficam em `useState` e somem ao recarregar. É deliberado: guardar conversas de
 * otimização exigiria uma tabela, uma política de retenção e uma decisão sobre quem pode ler
 * a pergunta de quem — e nada disso foi pedido. O que precisa persistir (recomendação, ação,
 * histórico) já persiste.
 */
export function PaginaMetaAdsOtimizador() {
  const { filtros, definir } = useFiltrosUrl()
  const avisos = useAvisos()

  const idPergunta = useId()
  const [pergunta, setPergunta] = useState('')
  const [conversa, setConversa] = useState<
    ReadonlyArray<{ readonly pergunta: string; readonly resposta: RespostaOtimizador }>
  >([])

  const contas = useQuery({
    queryKey: chaves.metaAds.contas,
    queryFn: ({ signal }) => metaAdsApi.contas(signal),
    staleTime: 300_000,
  })

  const perguntar = useMutation({
    mutationFn: (texto: string) =>
      metaAdsApi.perguntar({
        pergunta: texto,
        contaId: filtros.contaId === undefined ? undefined : Number.parseInt(filtros.contaId, 10),
        periodo: (filtros.periodo ?? '7D') as RespostaOtimizador['periodo']['periodo'],
      }),
    onSuccess: (resposta, texto) => {
      setConversa((atual) => [...atual, { pergunta: texto, resposta }])
      setPergunta('')
    },
    onError: () => {
      avisos.mostrar({
        tom: 'erro',
        titulo: 'Não foi possível responder',
        descricao:
          'A análise por IA pode estar desativada neste ambiente. As métricas, o score e as ' +
          'recomendações continuam disponíveis.',
      })
    },
  })

  const enviar = (texto: string) => {
    const limpo = texto.trim()
    if (limpo.length < 5) {
      avisos.mostrar({ tom: 'atencao', titulo: 'Escreva a pergunta com mais detalhe' })
      return
    }
    perguntar.mutate(limpo)
  }

  return (
    <>
      <CabecalhoPagina
        titulo="Otimizador"
        descricao="Pergunte sobre as contas. As respostas usam apenas os dados sincronizados."
      />

      <FiltrosMetaAds
        filtros={filtros}
        definir={definir}
        contas={contas.data?.dados ?? []}
        carregandoContas={contas.isLoading}
      />

      <Cartao className="mb-4">
        <label htmlFor={idPergunta} className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-conteudo-suave">
            Sua pergunta
          </span>
          <AreaTexto
            id={idPergunta}
            value={pergunta}
            onChange={(evento) => setPergunta(evento.target.value)}
            onKeyDown={(evento) => {
              // Ctrl/Cmd+Enter envia; Enter sozinho quebra linha. Pergunta de tráfego às
              // vezes tem duas frases, e enviar no Enter cortaria a segunda.
              if (evento.key === 'Enter' && (evento.metaKey || evento.ctrlKey)) {
                evento.preventDefault()
                enviar(pergunta)
              }
            }}
            rows={3}
            maxLength={500}
            placeholder="Ex.: qual campanha devo aumentar? onde estou desperdiçando dinheiro?"
          />
        </label>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {SUGESTOES.map((sugestao) => (
              <button
                key={sugestao}
                type="button"
                className="rounded-full border border-borda px-3 py-1 text-xs text-conteudo-suave hover:bg-superficie-2"
                onClick={() => {
                  setPergunta(sugestao)
                  enviar(sugestao)
                }}
              >
                {sugestao}
              </button>
            ))}
          </div>

          <Botao onClick={() => enviar(pergunta)} carregando={perguntar.isPending}>
            Perguntar
          </Botao>
        </div>
      </Cartao>

      {conversa.length === 0 && !perguntar.isPending ? (
        <Alerta tom="info">
          As respostas são geradas a partir das métricas já sincronizadas — nada é consultado
          na Meta ao vivo, e nenhum número é estimado. Se os dados não responderem à pergunta,
          o Otimizador diz isso em vez de arriscar.
        </Alerta>
      ) : null}

      <div className="space-y-4">
        {/* Mais recente primeiro: a última resposta é a que o gestor quer ler. */}
        {[...conversa].reverse().map((item, indice) => (
          <Troca key={`${conversa.length - indice}`} pergunta={item.pergunta} resposta={item.resposta} />
        ))}
      </div>
    </>
  )
}

/** As perguntas do §15, como atalhos. */
const SUGESTOES: ReadonlyArray<string> = [
  'Qual campanha devo aumentar?',
  'Qual anúncio devo pausar?',
  'Por que meu CPL aumentou?',
  'Onde estou desperdiçando dinheiro?',
  'Quais anúncios estão cansados?',
  'Me mostre os 5 melhores anúncios.',
]

function Troca({
  pergunta,
  resposta,
}: {
  readonly pergunta: string
  readonly resposta: RespostaOtimizador
}) {
  return (
    <Cartao semPadding>
      <CabecalhoCartao
        titulo={pergunta}
        descricao={
          <span className="text-xs">
            {resposta.periodo.de} a {resposta.periodo.ate} ({resposta.periodo.dias} dias)
          </span>
        }
      />
      <div className="space-y-3 p-4 sm:p-5">
        <div className="flex gap-2">
          <IconeFaisca className="mt-0.5 h-5 w-5 shrink-0 text-marca-600" />
          <p className="whitespace-pre-line text-sm leading-relaxed text-conteudo">
            {resposta.resposta}
          </p>
        </div>

        {!resposta.iaDisponivel ? (
          <Alerta tom="atencao">
            A análise por IA está desativada neste ambiente. As métricas, o score, o
            diagnóstico e as recomendações determinísticas continuam funcionando.
          </Alerta>
        ) : null}

        {/*
          A procedência da resposta, sempre visível. É o antídoto contra a suspeita de número
          inventado, e o §15 é explícito em que a IA nunca deve inventar número.
        */}
        <div className="border-t border-borda pt-3">
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-conteudo-fraco">
            Dados usados nesta resposta
          </p>
          <div className="flex flex-wrap gap-2">
            {resposta.dadosUsados.map((dado) => (
              <Etiqueta key={dado} tom="neutro" tamanho="sm">
                {dado}
              </Etiqueta>
            ))}
          </div>
        </div>
      </div>
    </Cartao>
  )
}
